import Vue from "vue";
import Router from "vue-router";
import config from "./config";
import Axios from "axios";
import store from "./store";

import Login from "./views/Login.vue";
import SelectStore from "./views/SelectStore.vue";
import Dashboard from "./views/Dashboard.vue";
import DashboardPro from "./views/DashboardPro.vue";
import Compare from "./views/Compare.vue";
import Users from "./views/Users.vue";
import Orders from "./views/Orders.vue";
import NotFound from "./views/NotFound.vue";
import { Trans } from "@/plugins/Translation";
import Help from "./views/Help.vue";
import Faq from "./views/Faq.vue";
import About from "./views/About.vue";
import Roles from "./views/Roles.vue";
import Organizations from "./views/Organizations.vue";
import Registration from "./views/Registration.vue";
import Profile from "./views/Profile.vue";
import PdfViewer from "./views/PdfViewer.vue";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: __dirname,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/:lang/dashboard-pro",
      name: "dashboardPro",
      component: DashboardPro,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
    },
    {
      path: "/:lang/dashboard",
      name: "dashboard",
      component: Dashboard,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
    },
    {
      path: "/:lang/dashboard/compare",
      name: "compare",
      component: Compare,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
    },
    {
      path: "/:lang/dashboard-pro/compare",
      name: "comparePro",
      component: Compare,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
    },
    {
      path: "/:lang/orders",
      name: "orders",
      component: Orders,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/users",
      name: "users",
      component: Users,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/roles",
      name: "roles",
      component: Roles,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/organizations",
      name: "organizations",
      component: Organizations,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/registration/:id",
      name: "registration",
      component: Registration,
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/help",
      name: "help",
      component: Help,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/faq",
      name: "faq",
      component: Faq,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/about",
      name: "about",
      component: About,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/select-store",
      name: "selectStore",
      component: SelectStore,
      meta: {
        requiresAuth: false,
        guest: true,
        header: false,
        footer: false,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/:lang/profile",
      name: "profile",
      component: Profile,
      meta: {
        requiresAuth: true,
        header: true,
        footer: true,
      },
      beforeEnter: Trans.routeMiddleware,
    },
    {
      path: "/",
      name: "login",
      component: Login,
      meta: {
        guest: true,
        header: false,
        footer: false,
      },
      beforeEnter: (to, from, next) => {
        if (localStorage.getItem("accessToken")) {
          next({
            path: Trans.getUserSupportedLang() + "/orders",
          });
        } else {
          next();
        }
      },
    },
    {
      path: "/pdf/:cmrId",
      name: "pdfViewer",
      component: PdfViewer,
      meta: {
        requiresAuth: false,
        header: false,
        footer: false,
      },
    },
    {
      path: "/404",
      component: NotFound,
      beforeEnter: (to, from, next) => {
        next({
          path: "/",
        });
      },
    },
    {
      // Redirect user to supported lang version.
      path: "/",
      /* eslint-disable */
      redirect(to) {
        if (localStorage.getItem("accessToken")) {
          return Trans.getUserSupportedLang() + "/orders";
        }
        return Trans.getUserSupportedLang();
      },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  try {
    if (!to.matched.length) return next("/404");

    // 1) requiresAuth
    if (to.matched.some((r) => r.meta.requiresAuth)) {
      if (!localStorage.getItem("accessToken")) return next({ path: "/" });
    }

    // 2) compare/dashboard switching
    if (
      to.name === "comparePro" &&
      localStorage.getItem("showNewDashboardSign") !== "true"
    ) {
      return next({ name: "compare" });
    }
    if (
      to.name === "compare" &&
      localStorage.getItem("showNewDashboardSign") === "true"
    ) {
      return next({ name: "comparePro" });
    }
    if (
      to.name === "dashboardPro" &&
      localStorage.getItem("showNewDashboardSign") !== "true"
    ) {
      return next({ name: "dashboard" });
    }
    if (
      to.name === "dashboard" &&
      localStorage.getItem("showNewDashboardSign") === "true"
    ) {
      return next({ name: "dashboardPro" });
    }

    // 3) selectStore/profile guards
    if (
      to.name === "selectStore" &&
      Object.keys(store.getters.GET_USER_ORGANIZATIONS).length === 0
    ) {
      return next({ name: "login" });
    }
    if (to.name === "profile" && localStorage.getItem("userId") === "0") {
      return next({ name: "login" });
    }

    // 4) user fetch (не блокируем навигацию при ошибке)
    if (
      localStorage.getItem("accessToken") &&
      localStorage.getItem("userId") !== "0" &&
      to.name !== "pdfViewer"
    ) {
      try {
        const resp = await Axios.get(config.apiBaseUrl + "user", {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        });
        if (resp.status === 200) store.commit("SET_LOGIN_USER", resp.data);
      } catch (e) {
        console.log("user fetch failed:", e);
      }
    }

    return next();
  } catch (e) {
    console.log("router.beforeEach error:", e);
    return next();
  }
});

export default router;
